import React from 'react';
import Fade from 'embla-carousel-fade';
import Autoplay from 'embla-carousel-autoplay';
import { CaretDownIcon, CaretUpIcon } from '@radix-ui/react-icons';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import Router from 'next/router';
import { Button } from '@/components/shadcn/button';
import { IRegion, IStats } from '@/types/explorer';
import { Input } from '@/components/shadcn/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/shadcn/table';
import {
  calculateCoverageArea,
  getMultiplierFactor,
  getRoundPercent,
  getTotalKm,
} from '@/utils/explorer';
import { Card } from '@/components/shadcn/card';
import SearchIcon from '@/components/ui/icons/SearchIcon';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '@/components/shadcn/breadcrumb';
import { P } from '@/components/shadcn/typography';
import { ICountry } from '@/types/geocode';
import { MapCarouselItem, mapItems } from '@/components/landing/MapCarousel';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/shadcn/carousel/carousel';
import { calculatePercentage } from '@/utils/number';
import DataBox from '../shared/DataBox';

const columns: ColumnDef<IRegion>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    accessorFn: row => row.name.trim(),
    cell: ({ row }) => {
      const metadata: any = row.original.metadata;
      const countries: any[] = metadata?.countries || [];
      const name: string = row.getValue('name');
      return (
        <div className="flex flex-col gap-0 overflow-hidden md:gap-1">
          <div className="truncate text-xs">{name}</div>
          <div className="truncate text-[10px] text-neutral-600">
            {countries.map(c => c.name).join(', ')}
          </div>
        </div>
      );
    },
    filterFn: (row, columnId, filterValue) => {
      const metadata: any = row.original.metadata;
      const countries: ICountry[] = metadata?.countries || [];
      const name: string = row.getValue(columnId);
      const countryNames = countries.map(c => c.name).join(', ');

      if (!filterValue) return true;

      return (
        name.toLowerCase().includes(filterValue.toLowerCase()) ||
        countryNames.toLowerCase().includes(filterValue.toLowerCase())
      );
    },
  },
  {
    id: 'mappedPercentage',
    accessorFn: row => {
      const mappedKm = getTotalKm(row.uniqueCoveredL12Cells);
      return calculatePercentage(mappedKm, row.osmKm);
    },
    header: '% Mapped',
    cell: ({ row }) => {
      const mappedKm = getTotalKm(row.original.uniqueCoveredL12Cells);
      const mappedPercentage = calculatePercentage(
        mappedKm,
        row.original.osmKm,
      );
      return <div className="text-xs md:text-sm">{`${mappedPercentage}%`}</div>;
    },
    enableSorting: true,
  },
  {
    accessorKey: 'multiplierFactor',
    header: 'Multiplier',
    cell: ({ row }) => {
      const multiplier = getMultiplierFactor(
        getRoundPercent(Number(row.getValue('multiplierFactor'))),
      );
      return <div className="text-xs md:text-sm">{multiplier}</div>;
    },
  },
];

interface Props {
  regions: IRegion[];
  stats?: IStats;
}

const PAGE_SIZE = 100;

function Regions(props: Props) {
  const { regions = [] } = props;
  const [sorting, setSorting] = React.useState<SortingState>([
    {
      id: 'mappedPercentage',
      desc: true,
    },
  ]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const table = useReactTable({
    data: regions,
    columns: columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    enableSortingRemoval: true,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: PAGE_SIZE,
      },
    },
  });

  if (regions.length === 0) return null;

  const {
    europe: europeanData,
    eastAsia: eastAsianData,
    canada: canadaData,
    usa: usaData,
  } = calculateCoverageArea(regions);

  const allRows = table.getCoreRowModel().rows.length;
  const filteredRows = table.getFilteredRowModel().rows.length;
  const isFiltered = allRows !== filteredRows;
  const pageIndex = table.getState().pagination.pageIndex;

  const from = pageIndex * PAGE_SIZE + 1;

  const to =
    from +
    Math.min((pageIndex + 1) * PAGE_SIZE, table.getRowModel().rows.length - 1);

  const total = isFiltered ? filteredRows : allRows;

  const regionsCoverageCards = [
    {
      title: 'United States of America',
      value: usaData,
    },
    {
      title: 'Canada',
      value: canadaData,
    },
    {
      title: 'Europe',
      value: europeanData,
    },
    {
      title: 'East Asia',
      value: eastAsianData,
    },
  ];

  return (
    <div className="flex h-full flex-col gap-2">
      <Breadcrumb className="flex h-6 shrink-0 items-center md:hidden">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink>Network</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Regions</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <div className="flex size-full max-w-2xl flex-col-reverse gap-2 pb-4 md:flex-row md:gap-6 md:pb-8">
        <div className="no-scrollbar flex w-full flex-col gap-2 overflow-visible text-neutral-0 md:gap-4 lg:w-1/2 lg:overflow-auto">
          <Breadcrumb className="hidden h-9 shrink-0 items-center md:flex">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink>Network</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Regions</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="relative flex w-full grow overflow-hidden rounded-lg border border-neutral-800">
            <Carousel
              showDots
              className="z-10 size-full"
              dotsClassName="absolute bottom-8"
              plugins={[
                Fade({
                  active: true,
                }),
                Autoplay({
                  playOnInit: true,
                  delay: 3000,
                }),
              ]}
              opts={{
                loop: true,
              }}>
              <CarouselContent className="z-10 ml-0 flex size-full grow">
                {[...mapItems].map((item, index) => (
                  <CarouselItem
                    className="box-content flex h-[296px] w-full grow pl-0 md:h-full lg:basis-full"
                    key={index}>
                    <MapCarouselItem
                      item={item}
                      className="hover:border-none"
                      forceOverlay
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
          <div className="flex flex-1 flex-col gap-2 md:gap-4">
            <div className="grid grid-cols-2 gap-2 md:gap-4">
              {regionsCoverageCards.map(card => (
                <DataBox
                  title={card.title}
                  value={`${Math.round(card.value)}%`}
                  key={card.title}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="flex h-1/2 w-full flex-col gap-2 md:gap-4 lg:h-full lg:basis-1/2">
          <div className="hidden h-9 shrink-0 items-center md:flex">
            <P>Explore active regions of the Hivemapper Network.</P>
          </div>
          <Card className="relative flex w-full items-center gap-2 px-4 text-neutral-0">
            <Input
              variant="dark"
              placeholder="Search for a region"
              value={
                (table.getColumn('name')?.getFilterValue() as string) ?? ''
              }
              onChange={event => {
                table.getColumn('name')?.setFilterValue(event.target.value);
                table.setPageIndex(0);
              }}
              className="border-none p-0 text-base leading-none placeholder:text-sm"
            />
            <SearchIcon className="size-4 fill-neutral-600" />
          </Card>
          <Card className="no-scrollbar h-[360px] overflow-scroll border border-neutral-800 md:h-full [&>div]:overflow-visible">
            <Table className="table-fixed bg-neutral-900">
              <TableHeader className="sticky top-0 bg-neutral-900">
                {table.getHeaderGroups().map(headerGroup => (
                  <TableRow
                    className="hover:bg-neutral-900"
                    key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => {
                      return (
                        <TableHead
                          key={header.id}
                          data-order={index}
                          onClick={() => header.column.toggleSorting()}
                          className="h-4 w-1/4 truncate p-3 leading-tight data-[order='0']:w-3/5 data-[order='1']:w-1/4 data-[order='2']:w-1/4 data-[order='3']:w-1/4 md:h-12 md:px-4 md:data-[order='1']:w-1/2 md:data-[order='2']:w-1/3 md:data-[order='3']:w-1/4">
                          <div className="flex cursor-pointer items-center gap-1 px-0 text-[10px] text-neutral-0 md:text-sm">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            {header.column.getIsSorted() === 'asc' ? (
                              <CaretUpIcon className="shrink-0" />
                            ) : header.column.getIsSorted() === 'desc' ? (
                              <CaretDownIcon className="shrink-0" />
                            ) : null}
                          </div>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map(row => (
                    <TableRow
                      key={row.id}
                      className="cursor-pointer leading-none hover:bg-neutral-800"
                      onClick={() =>
                        Router.push(
                          `/network/regions/${row.getValue('name')}/metrics`,
                        )
                      }
                      data-state={row.getIsSelected() && 'selected'}>
                      {row.getVisibleCells().map(cell => (
                        <TableCell
                          key={cell.id}
                          className="p-2 md:px-4 md:py-3">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="p-2 text-xs md:px-4 md:py-3 md:text-sm">
                      No regions found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
          <Card className="flex w-full items-center justify-end space-x-2 px-3 py-2 text-neutral-0 md:p-4">
            <div className="flex-1 text-[10px] md:text-xs">
              {total > PAGE_SIZE
                ? `${from} to ${to} of ${total} regions`
                : `${total} region${total === 1 ? '' : 's'}`}
            </div>
            <div className="space-x-2">
              <Button
                variant="default"
                size="sm"
                className="text-[10px] md:text-xs"
                onClick={table.previousPage}
                disabled={!table.getCanPreviousPage()}>
                Previous
              </Button>
              <Button
                variant="default"
                size="sm"
                className="text-[10px] md:text-xs"
                onClick={table.nextPage}
                disabled={!table.getCanNextPage()}>
                Next
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default Regions;
